import React from "react";
import { Chip, TextField } from "@mui/material";
import { SearchableDropdown } from "components";
import { getLinkChipMuiProps, LinkChipType } from "components/LinkChip";
import colors from "theme/colors";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { editTableAction } from "store/reducers/products/tables/tables.actions";

type DropdownOption = { value: string; name: string };

export function RulesTableSettingsInput({
  options,
  placeholder,
  currentValue,
  multiselect,
  rulesTableParameterType,
  ruleTableData,
  readOnly = false,
}: {
  options?: DropdownOption[];
  placeholder?: string;
  currentValue: any;
  multiselect?: boolean;
  rulesTableParameterType:
    | "rulesetIds"
    | "tableType"
    | "propertiesDisplaying"
    | "propertiesAdding"
    | "addNewProperty"
    | "lookUpFieldIndex";
  ruleTableData?: any;
  readOnly?: boolean;
}) {
  const dispatch = useDispatch();

  const handleChange = (e) => {
    if (readOnly) return;
    const newTable = {
      [rulesTableParameterType]: e,
    };
    dispatch(
      editTableAction({
        tableId: ruleTableData.id,
        updateData: newTable,
      })
    );
  };

  if (
    rulesTableParameterType === "tableType" ||
    rulesTableParameterType === "rulesetIds"
  ) {
    const linkChipType =
      rulesTableParameterType === "tableType"
        ? LinkChipType.TABLE_TYPE
        : LinkChipType.RULESET;
    const getChipProps = (option: DropdownOption) =>
      getLinkChipMuiProps(linkChipType, {
        id: option.value,
        name: option.name,
      });

    if (readOnly) {
      if (multiselect) {
        const selected = (currentValue as DropdownOption[]) ?? [];
        if (selected.length === 0) return null;
        return (
          <ReadOnlyChipRow>
            {selected.map((option) => (
              <Chip
                key={option.value}
                size="small"
                label={option.name}
                {...getChipProps(option)}
              />
            ))}
          </ReadOnlyChipRow>
        );
      }

      const selected = currentValue as DropdownOption | null;
      if (!selected) return null;
      return (
        <Chip size="small" label={selected.name} {...getChipProps(selected)} />
      );
    }

    // Split branches so SearchableDropdown's multiselect discriminated union type-checks.
    if (multiselect) {
      return (
        <StyledSearchableDropdown
          multiselect
          searchable
          placeholder={placeholder}
          options={options || []}
          value={(currentValue as DropdownOption[]) ?? []}
          handleValueChange={handleChange}
          id={rulesTableParameterType + "customDropdownUUID"}
          name={rulesTableParameterType}
          noShadow
          noHoverBorder
          getChipProps={getChipProps}
          disabled={readOnly}
        />
      );
    }

    return (
      <StyledSearchableDropdown
        multiselect={false}
        searchable
        placeholder={placeholder}
        options={options || []}
        value={(currentValue as DropdownOption | null) ?? null}
        handleValueChange={handleChange}
        id={rulesTableParameterType + "customDropdownUUID"}
        name={rulesTableParameterType}
        noShadow
        noHoverBorder
        getChipProps={getChipProps}
        disabled={readOnly}
      />
    );
  }

  if (rulesTableParameterType === "lookUpFieldIndex") {
    const maxProperties = ruleTableData?.properties?.length ?? 0;
    const minProperties = 1;
    const numericValue = Number(currentValue);
    const displayValue = Number.isFinite(numericValue)
      ? numericValue
      : minProperties;

    const commitLookupIndex = (raw: string) => {
      if (readOnly) return;
      if (raw.trim() === "") return;
      const next = Number(raw);
      if (!Number.isFinite(next) || !Number.isInteger(next)) return;
      if (next < minProperties || next > maxProperties) return;
      if (next === displayValue) return;
      handleChange(next);
    };

    if (readOnly) {
      return <ReadOnlyLookupValue>{displayValue}</ReadOnlyLookupValue>;
    }

    return (
      <TextField
        type="number"
        size="small"
        value={displayValue}
        onChange={(event) => commitLookupIndex(event.target.value)}
        inputProps={{
          min: minProperties,
          max: maxProperties,
          step: 1,
          "aria-label": "Lookup properties count",
        }}
        sx={{
          width: 88,
          "& .MuiOutlinedInput-root": {
            backgroundColor: colors.white,
            fontSize: 14,
            height: 28,
          },
          "& .MuiOutlinedInput-input": {
            py: 0,
            px: 1,
            height: 28,
            boxSizing: "border-box",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
        }}
      />
    );
  }

  return null;
}

const colorsPalette = {
  propertiesDisplaying: colors.tables.hoveredCell,
  addNewProperty: colors.tables.columnsAndRowsTitles,
  tableType: colors.white,
  rulesetIds: colors.white,
  propertiesAdding: colors.white,
};

// styled() erases SearchableDropdown generics; cast keeps call-site typing workable.
const StyledSearchableDropdown = styled(SearchableDropdown)`
  width: 100% !important;
  min-width: 0 !important;
  max-width: 100% !important;

  .MuiAutocomplete-root {
    width: 100% !important;
    max-width: 100% !important;
  }

  /* Compact input line-height so Table Type matches Rulesets; keep chip padding. */
  &&& .MuiAutocomplete-inputRoot {
    padding-top: 4px !important;
    padding-bottom: 4px !important;
    padding-left: 0;
    align-items: center !important;
    min-height: 0;
    box-sizing: border-box;
    /* Space between wrapped chip rows (and chips on the same row). */
    gap: 4px;
  }

  &&& .MuiAutocomplete-tag {
    margin: 0;
  }

  &&&& .MuiAutocomplete-input {
    line-height: 24px !important;
    height: 24px !important;
    min-height: 0 !important;
  }

  > div > div > input {
    background-color: ${({ name }) =>
      colorsPalette[name as keyof typeof colorsPalette] ??
      colors.white} !important;
    min-height: 0 !important;
    font-size: 12px !important;
    font-weight: ${(props) =>
      props.name === "addNewProperty" ? "700" : "normal"};
  }
` as typeof SearchableDropdown;

const ReadOnlyChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
`;

const ReadOnlyLookupValue = styled.span`
  font-size: 14px;
  line-height: 28px;
`;
